import React, { Component } from 'react';

import PropTypes from 'prop-types';

import { TrackEventContext } from 'common/containers/EventContainer';
import { ViewerContext } from 'common/containers/ViewerContainer';
import { LoadVideoContext, PlayVideoContext } from 'common/containers/WistiaContainer';
import IntercomLandingHelmet from 'common/helmets/IntercomLandingHelmet';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import Link from 'common/Link';
import Tappable from 'common/Tappable';
import UppercaseHeader from 'common/UppercaseHeader';
import devURL from 'common/util/devURL';
import withContexts from 'common/util/withContexts';
import FeatureHighlight3_1x from 'img/landing/feature-highlight-3-1x.webp';
import FeatureHighlight3_2x from 'img/landing/feature-highlight-3-2x.webp';
import Intercom1 from 'img/landing/intercom-1.webp';
import Intercom2 from 'img/landing/intercom-2.webp';
import IntercomVideoThumbnail from 'img/landing/intercom-video-thumbnail.webp';

import IntercomLogo from 'img/canny-intercom-integration.png';

const VideoID = '7927uj57u4';

import 'css/components/integrations/_IntegrationsLanding.scss';
import 'css/components/_LandingFeatures.scss';

class IntercomLanding extends Component {
  static propTypes = {
    loadVideo: PropTypes.func,
    playVideo: PropTypes.func,
    trackEvent: PropTypes.func,
    viewer: PropTypes.shape({
      companies: PropTypes.array,
    }),
  };

  componentDidMount() {
    const { trackEvent } = this.props;
    trackEvent('Viewed Intercom Integration Page');
  }

  onLoadVideo = () => {
    const { loadVideo, trackEvent } = this.props;
    loadVideo(VideoID, (video) => {
      if (video.trackingEvents) {
        return;
      }
      video.trackingEvents = true;
      video.bind('play', () => {
        trackEvent('Started Intercom Video');
        return video.unbind;
      });
      video.bind('secondchange', (seconds) => {
        if (seconds !== 80) {
          return;
        }
        trackEvent('Finished Intercom Video');
        return video.unbind;
      });
    });
  };

  onPlayVideo = () => {
    this.onLoadVideo();

    const { playVideo } = this.props;
    playVideo(VideoID);
  };

  renderVideo() {
    return (
      <div className="playButtonContainer">
        <div className="playButton" />
        <div className="playButtonLabel">
          <UppercaseHeader>See how it works</UppercaseHeader>
        </div>
      </div>
    );
  }

  renderCTA() {
    const { viewer } = this.props;
    if (!viewer || viewer.loggedOut || viewer.companies.length === 0) {
      // logged out || no companies -> register
      return (
        <Link className="LandingGlobal__cta" to="/register">
          Get started
        </Link>
      );
    }

    // logged in && has company -> admin dashboard (assume first company for now)
    const { companies } = viewer;
    const company = companies[0];
    const settingsURL = devURL(
      'https://' + company.subdomain + '.canny.io/admin/settings/intercom'
    );

    return (
      <a className="LandingGlobal__cta" href={settingsURL}>
        Get started
      </a>
    );
  }

  render() {
    return (
      <div className="integrationsLanding">
        <IntercomLandingHelmet />
        <div className="integrationsLandingContainer">
          <div className="header">
            <div className="left">
              <LazyLoadedImage className="appLogo" loading="eager" src={IntercomLogo} />
              <div className="text">
                <h1 className="title">Canny for Intercom</h1>
                <div className="subtitle">Capture feedback from people in&nbsp;Intercom</div>
                <div className="links">
                  {this.renderCTA()}
                  <a
                    href="https://help.canny.io/en/articles/2048046-intercom-integration"
                    rel="noopener"
                    target="_blank"
                    className="LandingIntegrations__install-link">
                    See install docs
                  </a>
                </div>
              </div>
            </div>
            <Tappable onTap={this.onPlayVideo}>
              <div className="right" onMouseOver={this.onLoadVideo}>
                {this.renderVideo()}
                <LazyLoadedImage loading="eager" src={IntercomVideoThumbnail} />
              </div>
            </Tappable>
          </div>
          <div className="features">
            <div className="featureHighlight">
              <div className="featureImage">
                <LazyLoadedImage loading="eager" src={Intercom1} />
              </div>
              <div className="featureText">
                <div className="title">Track feedback from within&nbsp;conversations</div>
                <div className="subtitle">
                  Don't let feedback slip through the cracks. Keep your product team informed and
                  your users&nbsp;happy.
                </div>
              </div>
            </div>
            <div className="featureHighlight">
              <div className="featureImage">
                <LazyLoadedImage src={Intercom2} />
              </div>
              <div className="featureText">
                <div className="title">Let users give feedback&nbsp;directly</div>
                <div className="subtitle">
                  Less friction means more feedback. It'll be tracked in an organized way that's
                  easy for your product team to&nbsp;understand.
                </div>
              </div>
            </div>
            <div className="featureHighlight">
              <div className="featureImage">
                <LazyLoadedImage
                  src={FeatureHighlight3_1x}
                  srcSet={`${FeatureHighlight3_1x} 1x, ${FeatureHighlight3_2x} 2x`}
                  alt="Keeping customers up to date with features in Canny, a customer feedback management tool"
                />
              </div>
              <div className="featureText">
                <div className="title">Keep everyone engaged with product&nbsp;updates</div>
                <div className="subtitle">
                  Automatically follow up with people when you release a feature. Keep the feedback
                  loop going&nbsp;strong.
                </div>
              </div>
            </div>
          </div>
          <LandingFooterCTA style="solid" />
        </div>
      </div>
    );
  }
}

export default withContexts({
  loadVideo: LoadVideoContext,
  playVideo: PlayVideoContext,
  trackEvent: TrackEventContext,
  viewer: ViewerContext,
})(IntercomLanding);
